const EMBED_SOURCE = 'ace-payments-embed';
const PARENT_SOURCE = 'ace-payments-embed-parent';

export type EmbedMessageType = 'READY' | 'RESIZE' | 'PAYMENT_SUCCESS' | 'PAYMENT_ERROR' | 'VALIDATION_ERROR' | 'SOFT_ERROR';
export type ParentMessageType = 'CONFIGURE' | 'START';

export interface EmbedPaymentMessage {
  source: typeof EMBED_SOURCE;
  type: EmbedMessageType;
  payload: Record<string, unknown>;
}

export interface ParentMessage {
  source: typeof PARENT_SOURCE;
  type: ParentMessageType;
  payload: {
    ctaText?: string;
  };
}

function getAllowedOrigins(): string[] {
  return (import.meta.env.VITE_EMBED_ALLOWED_ORIGINS || '').split(',').filter(Boolean);
}

export function isOriginAllowed(origin: string): boolean {
  if (!origin) return false;

  const enforceHttps = import.meta.env.VITE_ENV === 'prod';
  let parsedOrigin: URL | null = null;
  try {
    parsedOrigin = new URL(origin);
  } catch {
    return false;
  }

  if (enforceHttps && parsedOrigin.protocol !== 'https:') return false;

  // Same-origin is always allowed (covers local dev on any port)
  if (origin === window.location.origin) return true;

  const hostname = parsedOrigin.hostname;

  return getAllowedOrigins().some((pattern: string) => {
    if (pattern.startsWith('*.')) {
      const suffix = pattern.slice(1); // e.g., ".ace.aaa.com"
      return hostname.endsWith(suffix) || hostname === suffix.slice(1);
    }
    try {
      const allowedOrigin = new URL(pattern);
      if (enforceHttps && allowedOrigin.protocol !== 'https:') return false;
      return allowedOrigin.origin === origin;
    } catch {
      return false;
    }
  });
}

let parentOrigin: string | null = null;

export function setParentOrigin(origin: string): void {
  parentOrigin = origin;
}

export function getParentOrigin(): string | null {
  return parentOrigin;
}

export function postToParent(type: EmbedMessageType, payload: Record<string, unknown> = {}): void {
  const target = parentOrigin;
  if (!target || !isOriginAllowed(target)) return;

  const message: EmbedPaymentMessage = { source: EMBED_SOURCE, type, payload };
  window.parent.postMessage(message, target);
}

export function isParentMessage(data: unknown): data is ParentMessage {
  if (!data || typeof data !== 'object') return false;
  const msg = data as Record<string, unknown>;
  return msg.source === PARENT_SOURCE && typeof msg.type === 'string';
}

// Eagerly set parentOrigin from document.referrer when loaded in an iframe
if (typeof window !== 'undefined' && window.parent !== window) {
  try {
    const origin = new URL(document.referrer).origin;
    if (isOriginAllowed(origin)) {
      setParentOrigin(origin);
    }
  } catch {
    // invalid referrer — will be set later via message handshake
  }
}
